import React from "react";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";
import _ from "lodash";
import { toast } from "react-toastify";

import { ProductSearchComponent } from "src/core/products/components/ProductSearchComponent";
import { IProduct } from "src/core/products/api/products.api";
import { ITerminology } from "src/library/utils";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";

import {
  IWasteCreate,
  WasteReasonCode
} from "../api/waste.api";

export interface NewWastedProductRowProps {
  /** more efficient to pass in the list of available products */
  availableProducts: IProduct[];
  branchId: number;
  terminology: ITerminology;
  currentProductIds: number[];
  wasteDate?: Date;
  updateList: (data: IWasteCreate) => void;
  isLoading?: boolean;
}

/**
 * Renders a row for adding a new wasted product
 */
const NewWastedProductRow: React.ReactFCWithChildren<NewWastedProductRowProps> = ({
  availableProducts,
  branchId,
  terminology,
  currentProductIds,
  wasteDate,
  updateList,
  isLoading
}) => {
  const [ searchTerm, setSearchTerm ] = React.useState("");
  const [ filteredAvailableProducts, setFilteredAvailableProducts ] = React.useState<IProduct[]>([]);

  // handle selecting a product
  const productDownShiftProps = useCombobox<IProduct>({
    items: filteredAvailableProducts,
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        if (currentProductIds.length) {
          // avoid duplicate products in list
          const productAlreadyExists = _.find(currentProductIds, [ "productId", changes.selectedItem.id ]);

          if (productAlreadyExists) {
            toast.error(`The product ${changes.selectedItem.partNumber} has already been recorded for this date.`);

            return;
          }
        }

        if (!wasteDate) {
          toast.error("Unable to add product: make sure to set a waste date before trying to add a product.");

          return;
        }

        updateList({
          branchId,
          wasteDate,
          product: changes.selectedItem,
          productId: changes.selectedItem.id,
          quantity: 1,
          reasonCode: WasteReasonCode.OutOfDate
        });

        return;
      }
    }
  });

  // Filter out all available products based on the current search term
  React.useEffect(() => {
    if (searchTerm.length === 0) {
      setFilteredAvailableProducts(availableProducts);
    } else {
      const filtered = availableProducts.filter(product => {
        const formattedDescription = product.description.toLowerCase();
        const formattedPartNumber = product.partNumber?.toLowerCase() || "";
        const formattedSearchTerm = searchTerm.toLowerCase();

        return formattedDescription.includes(formattedSearchTerm) || formattedPartNumber.includes(formattedSearchTerm);
      });

      setFilteredAvailableProducts(filtered);
    }
  }, [ searchTerm, availableProducts ]);

  return (
    <RowWrapper>
      {isLoading ? (
        <div className="loading-wrapper">
          <LoadingSpinner
            type="flat"
            position="inline"
          />
        </div>
      ) : (
        <div>
          <ProductSearchComponent
            error={null}
            loading={false}
            minimumChars={0}
            onSearch={setSearchTerm}
            searchResults={filteredAvailableProducts}
            downshiftProps={productDownShiftProps}
            placeholder={`Search ${terminology && terminology.product.plural.text}...`}
          />
        </div>
      )}
    </RowWrapper>
  );
};

const RowWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    padding: ${theme.spacing.padding.sm};
    background: ${theme.colors.white};
    border-bottom: ${theme.colors.lightGrey} 1px solid;
    min-width: 100%;
    display: flex;
    align-items: center;
    flex-flow: row wrap;

    .loading-wrapper {
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }
  `}
`;

export default NewWastedProductRow;
